import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUser, logout } from '../services/api';
import { BRAND_NAME } from '../branding';
import { DEFAULT_PASSWORD_SUFFIX, defaultPasswordFor } from '../utils/passwordPolicy';
import { showSuccess } from '../components/ToastNotifications';
import './Login.css';

// Operator rule 2026-10-24: the CRM must always have at least two
// administrators. While fewer exist, App.js renders ONLY this page for a
// signed-in administrator (the server refuses every other call with 403
// SECOND_ADMIN_REQUIRED). Creating the account needs a passkey — the global
// prompt appears on submit.
function SecondAdminRequired({ user, setUser }) {
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const minAdmins = user?.minAdmins || 2;

  const handleSignOut = async () => {
    try { await logout(); } catch (err) { /* best-effort */ }
    setUser(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const r = await createUser({ username: username.trim(), role: 'administrator' });
      showSuccess(`Administrator ${r?.username || username} created. First password: ${defaultPasswordFor(username)}`);
      setUser({ ...user, secondAdminRequired: false, adminCount: (user?.adminCount || 1) + 1 });
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create the administrator');
      setBusy(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-brand">{BRAND_NAME}</div>
        <h1 className="login-title">Create a second administrator</h1>
        <p className="login-subtitle">
          The CRM must always have at least {minAdmins} administrators, so one can always help the other (reset a lost authenticator or passkey) and no account is ever the only key. Right now you are the only one. Create the second administrator account before you continue — nothing else works until then.
        </p>

        {error && <div className="login-error" role="alert">{error}</div>}

        <form onSubmit={submit} className="login-form" autoComplete="off">
          <div className="form-group">
            <label htmlFor="secondAdminUsername" className="form-label">Username</label>
            <input
              type="text"
              id="secondAdminUsername"
              className="form-control"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
              minLength={2}
              maxLength={32}
              pattern="[A-Za-z0-9._-]{2,32}"
              autoComplete="off"
            />
          </div>
          <div className="form-group">
            <div className="form-label">First password</div>
            <div>{username.trim() ? defaultPasswordFor(username) : `username + ${DEFAULT_PASSWORD_SUFFIX}`}</div>
            <small className="form-text" style={{ display: 'block', marginTop: 4 }}>
              Set automatically: the username followed by {DEFAULT_PASSWORD_SUFFIX}. It works for 24 hours; they must replace it at their first sign-in, then set up their authenticator app and passkey.
            </small>
          </div>
          <button type="submit" className="btn btn-primary btn-block" disabled={busy || !username.trim()}>
            {busy ? 'Creating…' : 'Create administrator'}
          </button>
        </form>
        <small className="tf-hint" style={{ display: 'block', marginTop: 'var(--spacing-md, 12px)' }}>
          You will be asked to confirm with your passkey.
        </small>

        <div style={{ textAlign: 'center', marginTop: 'var(--spacing-lg, 16px)' }}>
          <button type="button" className="btn btn-outline btn-small" onClick={handleSignOut}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

export default SecondAdminRequired;
